import { FC, useMemo } from 'react';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import {
  getCashFlowFlagsForInstrument,
  getWaterfallCaseYearInstrumentValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { l } from '@locale/setupI18n';
import { Pwerm2CalculationResultsDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { enumKeyByValue } from '@app/shared/helpers';
import { InstrumentType } from '@app/shared/models/contracts/enums/shared-enums';

export const CashFlowMovementValuationSection: FC<{
  instrument: InstrumentDefinitionWithId;
  results: Pwerm2CalculationResultsDto;
}> = ({ instrument, results }) => {
  const isOrdinaryEquity =
    instrument.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);

  const cashFlowFlags = useMemo(
    () => getCashFlowFlagsForInstrument(instrument.instrumentId, results),
    [results, instrument.instrumentId]
  );

  return (
    <>
      {cashFlowFlags.hasInfusions && (
        <TableSectionValuesPerForecast
          title={l('_Infusion')}
          highlight
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.cashFlowSummary?.totalAdditions
              )}
              dataTestId={`additions-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}
      {cashFlowFlags.hasRedemptions && (
        <TableSectionValuesPerForecast
          title={isOrdinaryEquity ? l('_Buyback') : l('_Redemption')}
          highlight
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.cashFlowSummary?.totalRedemptions
              )}
              dataTestId={`redemptions-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}
      {!isOrdinaryEquity && cashFlowFlags.hasCashPayInterest && (
        <TableSectionValuesPerForecast
          title={l('_CashInterest')}
          highlight
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.cashFlowSummary?.cashPayInterest
              )}
              dataTestId={`cash-pay-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}
      {isOrdinaryEquity && cashFlowFlags.hasDividends && (
        <TableSectionValuesPerForecast
          title={l('_Dividends')}
          highlight
          renderSpacer={false}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.cashFlowSummary?.dividends
              )}
              dataTestId={`dividends-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}
    </>
  );
};
